// /src/components/donate/SupportChoicePopup.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../authContext';
import { useSecurity } from '../../SecurityContext';
import DonorGemIcon from '../icons/DonorGemIcon';
import useSupportChoiceSocket from './supportChoice/useSupportChoiceSocket';
import {
  confirmSupportChoiceForPopup,
  fetchMySupportStatusForPopup,
} from './supportChoice/supportChoiceApi';
import {
  SUPPORT_CHOICE_OPEN_CHANGE,
  emitSupportChoiceStatusChanged,
} from './supportChoice/supportChoiceEvents';
import RosterProfileThemeCustomizeModal from '../../pages/private/members/rosterTools/components/rosterFileTools/RosterProfileThemeCustomizeModal';

function levelLabel(level) {
  if (level === 'supporter') return 'Supporter';
  if (level === 'patron') return 'Patron';
  if (level === 'benefactor') return 'Benefactor';
  return 'Member';
}

function optionGemVariant(level) {
  if (level === 'benefactor') return 'premium';
  if (level === 'patron') return 'subscriber';
  return 'one-time';
}

function optionTone(level) {
  if (level === 'benefactor') return 'gold';
  if (level === 'patron') return 'purple';
  return 'blue';
}

function levelRank(level) {
  if (level === 'benefactor') return 3;
  if (level === 'patron') return 2;
  if (level === 'supporter') return 1;
  return 0;
}

function highestAvailableLevel(options) {
  if (!Array.isArray(options) || options.length === 0) return '';
  const selectable = options.filter((option) => option?.selectable !== false);
  const pool = selectable.length > 0 ? selectable : options;
  return pool.reduce((best, option) => (
    levelRank(option?.level) > levelRank(best) ? option.level : best
  ), pool[0]?.level || '');
}

function optionBenefits(level) {
  if (level === 'supporter') {
    return [
      'Supporter themes',
      'Future Supporter rewards',
    ];
  }
  if (level === 'patron') {
    return [
      'Supporter + Patron themes',
      'Future Supporter + Patron rewards',
    ];
  }
  if (level === 'benefactor') {
    return [
      'Supporter + Patron + Benefactor themes',
      'Future Supporter + Patron + Benefactor rewards',
    ];
  }
  return [];
}

function formatDate(value) {
  if (!value) return '—';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString();
}

function pluralUnit(value, singular, plural) {
  return value === 1 ? singular : plural;
}

/** Human duration from whole days: years, months, days (no decimal months). */
function formatDuration(option) {
  let days = Math.max(0, Math.round(Number(option?.durationDays) || 0));
  if (days <= 0) {
    const monthsApprox = Number(option?.durationMonths);
    if (Number.isFinite(monthsApprox) && monthsApprox > 0) {
      days = Math.max(1, Math.round(monthsApprox * 30.4375));
    }
  }
  if (days <= 0) return '—';

  const years = Math.floor(days / 365);
  days %= 365;
  const months = Math.floor(days / 30);
  days %= 30;

  const parts = [];
  if (years > 0) parts.push(`${years} ${pluralUnit(years, 'year', 'years')}`);
  if (months > 0) parts.push(`${months} ${pluralUnit(months, 'month', 'months')}`);
  if (days > 0 || parts.length === 0) {
    parts.push(`${days} ${pluralUnit(days, 'day', 'days')}`);
  }
  return parts.join(', ');
}

function ShieldIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="support-choice-popup__icon-svg">
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
        d="M12 3.2 19 6.2v5.1c0 4.5-3 7.8-7 9.5-4-1.7-7-5-7-9.5V6.2L12 3.2Z"
      />
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m8.8 12.1 2.2 2.2 4.3-4.4"
      />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="support-choice-popup__icon-svg">
      <circle cx="12" cy="12" r="8.2" fill="none" stroke="currentColor" strokeWidth="1.7" />
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        d="M12 7.8v4.5l3.1 1.9"
      />
    </svg>
  );
}

export default function SupportChoicePopup() {
  const { isLoggedIn } = useAuth();
  const { isViewAsActive, viewAsUserId, viewAsLoading } = useSecurity();
  const [support, setSupport] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');
  const [manualChangeOpen, setManualChangeOpen] = useState(false);
  const [themeModalOpen, setThemeModalOpen] = useState(false);
  const [autoConfirmFailed, setAutoConfirmFailed] = useState(false);
  const autoConfirmKeyRef = useRef('');
  const autoConfirmInFlightRef = useRef(false);

  const mergeSupport = useCallback((next) => {
    setSupport((prev) => {
      if (!next) return null;
      return {
        ...prev,
        ...next,
        lifetimeTotalEur: next.lifetimeTotalEur ?? prev?.lifetimeTotalEur,
        highestSupportLevelEverReached:
          next.highestSupportLevelEverReached ?? prev?.highestSupportLevelEverReached,
        currentSupportUntil: next.currentSupportUntil ?? prev?.currentSupportUntil,
        changeOptions: Array.isArray(next.changeOptions)
          ? next.changeOptions
          : (prev?.changeOptions || []),
      };
    });
  }, []);

  const onSupportStatus = useCallback((next) => {
    mergeSupport(next);
    const pending = Array.isArray(next?.pendingChoiceOptions) ? next.pendingChoiceOptions : [];
    if (pending.length > 0) {
      setManualChangeOpen(false);
      setSelectedLevel((prev) => {
        if (pending.some((option) => option.level === prev)) return prev;
        return highestAvailableLevel(pending);
      });
      if (pending.length !== 1) {
        setAutoConfirmFailed(false);
        autoConfirmKeyRef.current = '';
      }
    }
    if (!next?.hasPendingChoice) {
      setError('');
      setAutoConfirmFailed(false);
      autoConfirmKeyRef.current = '';
    }
  }, [mergeSupport]);

  useSupportChoiceSocket({
    enabled: isLoggedIn,
    isLoggedIn,
    isViewAsActive,
    viewAsUserId,
    viewAsLoading,
    onSupportStatus,
  });

  useEffect(() => {
    if (!isLoggedIn) {
      setManualChangeOpen(false);
      return undefined;
    }

    const onOpenChange = async () => {
      setError('');
      setManualChangeOpen(true);
      try {
        const data = await fetchMySupportStatusForPopup();
        if (data?.ok && data.data) {
          mergeSupport(data.data);
          const options = Array.isArray(data.data.changeOptions) ? data.data.changeOptions : [];
          const preferred = options.find((option) => option.isCurrent && option.selectable)
            || options.find((option) => option.selectable);
          setSelectedLevel(preferred?.level || highestAvailableLevel(options));
        }
      } catch (loadError) {
        setError(
          loadError?.response?.data?.error
          || loadError?.message
          || 'Could not load support change options.',
        );
      }
    };

    window.addEventListener(SUPPORT_CHOICE_OPEN_CHANGE, onOpenChange);
    return () => window.removeEventListener(SUPPORT_CHOICE_OPEN_CHANGE, onOpenChange);
  }, [isLoggedIn, mergeSupport]);

  const pendingOptions = useMemo(
    () => (Array.isArray(support?.pendingChoiceOptions) ? support.pendingChoiceOptions : []),
    [support],
  );
  const changeOptions = useMemo(
    () => (Array.isArray(support?.changeOptions) ? support.changeOptions : []),
    [support],
  );

  const isPendingMode = Boolean(support?.hasPendingChoice && pendingOptions.length > 0);
  const isChangeMode = Boolean(
    !isPendingMode
    && manualChangeOpen
    && support?.canChangeActiveSupport
    && changeOptions.length > 0,
  );
  const choiceMode = isPendingMode ? 'pending' : 'change';
  const options = isPendingMode ? pendingOptions : changeOptions;
  const solePendingOption = isPendingMode && pendingOptions.length === 1
    ? pendingOptions[0]
    : null;
  const shouldAutoConfirmPending = Boolean(
    solePendingOption?.level
    && !autoConfirmFailed
    && !(isViewAsActive && viewAsLoading),
  );

  const lifetimeLevel = levelLabel(support?.highestSupportLevelEverReached);
  const hasActive = Boolean(support?.currentSupportLevel);

  // Hide popup while a sole pending option is auto-confirmed; show only on failure fallback.
  const isOpen = Boolean(
    isLoggedIn
    && !(isViewAsActive && viewAsLoading)
    && (isChangeMode || (isPendingMode && (pendingOptions.length > 1 || autoConfirmFailed))),
  );

  useEffect(() => {
    if (!isOpen) setThemeModalOpen(false);
  }, [isOpen]);

  const applyConfirmedSupport = useCallback((data) => {
    mergeSupport({
      ...(data?.data || {}),
      hasPendingChoice: false,
      pendingChoiceOptions: [],
      pendingTransactionCount: 0,
    });
    setSelectedLevel('');
    setManualChangeOpen(false);
    setAutoConfirmFailed(false);
    autoConfirmKeyRef.current = '';
    emitSupportChoiceStatusChanged(data?.data || null);
  }, [mergeSupport]);

  useEffect(() => {
    if (!isLoggedIn || !shouldAutoConfirmPending) return undefined;
    if (autoConfirmInFlightRef.current) return undefined;

    const level = solePendingOption.level;
    const autoKey = `pending:${level}:${support?.pendingTransactionCount || 0}`;
    if (autoConfirmKeyRef.current === autoKey) return undefined;

    autoConfirmInFlightRef.current = true;
    let cancelled = false;

    (async () => {
      setConfirming(true);
      setError('');
      try {
        const data = await confirmSupportChoiceForPopup(level, 'pending');
        if (cancelled) return;
        if (!data?.ok) {
          throw new Error(data?.error || 'Could not confirm support choice.');
        }
        autoConfirmKeyRef.current = autoKey;
        applyConfirmedSupport(data);
      } catch (confirmError) {
        if (cancelled) return;
        setAutoConfirmFailed(true);
        setSelectedLevel(level);
        setError(
          confirmError?.response?.data?.error
          || confirmError?.message
          || 'Could not confirm support choice.',
        );
      } finally {
        autoConfirmInFlightRef.current = false;
        if (!cancelled) setConfirming(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    applyConfirmedSupport,
    isLoggedIn,
    shouldAutoConfirmPending,
    solePendingOption,
    support?.pendingTransactionCount,
  ]);

  const handleClose = () => {
    if (isPendingMode || confirming || themeModalOpen) return;
    setManualChangeOpen(false);
    setError('');
  };

  const handleConfirm = async () => {
    if (!selectedLevel || confirming) return;
    const selectedOption = options.find((option) => option.level === selectedLevel);
    if (choiceMode === 'change' && selectedOption?.selectable === false) return;
    if (choiceMode === 'change' && selectedOption?.isCurrent) {
      setManualChangeOpen(false);
      return;
    }

    setConfirming(true);
    setError('');
    try {
      const data = await confirmSupportChoiceForPopup(selectedLevel, choiceMode);
      if (!data?.ok) {
        throw new Error(data?.error || 'Could not confirm support choice.');
      }
      applyConfirmedSupport(data);
    } catch (confirmError) {
      setError(
        confirmError?.response?.data?.error
        || confirmError?.message
        || 'Could not confirm support choice.',
      );
    } finally {
      setConfirming(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {createPortal(
    <div
      className="support-choice-popup-overlay"
      role="presentation"
      onClick={isChangeMode && !themeModalOpen ? handleClose : undefined}
    >
      <div
        className="support-choice-popup"
        role="dialog"
        aria-modal="true"
        aria-labelledby="support-choice-popup-title"
        data-resource="public.donation-panel"
        data-action="read"
        data-route="/api/kofi/my-support-status"
        data-description="Support choice popup"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="support-choice-popup__shell">
          {isChangeMode ? (
            <button
              type="button"
              className="support-choice-popup__close"
              onClick={handleClose}
              aria-label="Close"
              data-resource="public.donation-panel"
              data-action="read"
            >
              ×
            </button>
          ) : null}

          <header className="support-choice-popup__header">
            <p className="support-choice-popup__eyebrow">
              Thank you for your support
            </p>
            <h3 id="support-choice-popup-title" className="support-choice-popup__title">
              {isChangeMode
                ? 'Change your active support level'
                : support?.currentSupportLevel
                  ? 'Apply your latest contribution'
                  : 'Choose how to apply your contribution'}
            </h3>
            <p className="support-choice-popup__lead">
              {isChangeMode
                ? 'Your remaining active time is converted between tiers. Upgrades are always available; downgrades unlock after one month at your current level.'
                : 'Lifetime recognition stays with you. Active support is temporary: each tier unlocks its theme libraries and rewards for a set duration.'}
            </p>
          </header>

          <div className="support-choice-popup__status-grid">
            <section className="support-choice-popup__stat" aria-label="Lifetime recognition">
              <header className="support-choice-popup__stat-header">
                <span className="support-choice-popup__stat-icon" aria-hidden>
                  <ShieldIcon />
                </span>
                <h4 className="support-choice-popup__stat-title">Lifetime</h4>
              </header>
              <p className="support-choice-popup__stat-value">{lifetimeLevel}</p>
              <p className="support-choice-popup__stat-meta">
                Total contributed
                <strong>€{Number(support?.lifetimeTotalEur || 0).toFixed(2)}</strong>
              </p>
            </section>

            <section className="support-choice-popup__stat" aria-label="Active support">
              <header className="support-choice-popup__stat-header">
                <span className="support-choice-popup__stat-icon" aria-hidden>
                  <ClockIcon />
                </span>
                <h4 className="support-choice-popup__stat-title">Active</h4>
              </header>
              {hasActive ? (
                <>
                  <p className="support-choice-popup__stat-value">
                    {levelLabel(support.currentSupportLevel)}
                  </p>
                  <p className="support-choice-popup__stat-meta">
                    Until
                    <strong>{formatDate(support.currentSupportUntil)}</strong>
                  </p>
                </>
              ) : (
                <>
                  <p className="support-choice-popup__stat-value support-choice-popup__stat-value--muted">
                    Selection required
                  </p>
                  <p className="support-choice-popup__stat-meta">
                    Pick a tier below to activate temporary perks.
                  </p>
                </>
              )}
            </section>
          </div>

          <section className="support-choice-popup__choice" aria-label="Support choice">
            <div className="support-choice-popup__choice-head">
              <h4 className="support-choice-popup__choice-title">
                {isChangeMode ? 'Reallocate remaining time' : 'Active support options'}
              </h4>
              <p className="support-choice-popup__choice-note">
                {isChangeMode
                  ? (
                    support?.canDowngrade
                      ? 'Pick a tier. Duration is recalculated from your remaining active support.'
                      : `Upgrades are available now. Downgrades unlock on ${formatDate(support?.downgradeAvailableAt)}.`
                  )
                  : 'Same contribution — pick a tier for duration and which theme libraries you unlock.'}
              </p>
              <button
                type="button"
                className="support-choice-popup__themes-btn"
                onClick={() => setThemeModalOpen(true)}
                data-resource="private.settings.account"
                data-action="read"
                data-route="/api/user/settings/profile-theme"
                data-description="Browse profile themes from support choice"
              >
                Browse profile themes
              </button>
            </div>

            <div className="support-choice-popup__options" role="radiogroup" aria-label="Support tier">
              {options.map((option) => {
                const selected = selectedLevel === option.level;
                const tone = optionTone(option.level);
                const gemVariant = optionGemVariant(option.level);
                const benefits = optionBenefits(option.level);
                const locked = option.selectable === false;
                return (
                  <label
                    key={option.level}
                    className={[
                      'support-choice-popup__option',
                      `support-choice-popup__option--${tone}`,
                      selected ? 'support-choice-popup__option--selected' : '',
                      locked ? 'support-choice-popup__option--locked' : '',
                      option.isCurrent ? 'support-choice-popup__option--current' : '',
                    ].filter(Boolean).join(' ')}
                  >
                    <input
                      type="radio"
                      name="support-choice-popup-level"
                      className="support-choice-popup__option-input"
                      checked={selected}
                      disabled={locked}
                      onChange={() => setSelectedLevel(option.level)}
                    />
                    <span className="support-choice-popup__option-main">
                      <span className="support-choice-popup__option-top">
                        <span className="support-choice-popup__option-identity">
                          <DonorGemIcon
                            className={[
                              'support-choice-popup__option-gem',
                              'donor-gem-badge__gem',
                              `donor-gem-badge__gem--${gemVariant}`,
                            ].join(' ')}
                          />
                          <span className="support-choice-popup__option-name">
                            {levelLabel(option.level)}
                            {option.isCurrent ? ' · current' : ''}
                          </span>
                        </span>
                        <span className="support-choice-popup__option-duration">
                          {formatDuration(option)}
                        </span>
                      </span>
                      <ul className="support-choice-popup__option-benefits">
                        {benefits.map((line) => (
                          <li key={line} className="support-choice-popup__option-benefit">
                            <span className="support-choice-popup__option-check" aria-hidden>✓</span>
                            <span>{line}</span>
                          </li>
                        ))}
                      </ul>
                      {locked && option.lockedReason ? (
                        <p className="support-choice-popup__option-lock">{option.lockedReason}</p>
                      ) : null}
                    </span>
                  </label>
                );
              })}
            </div>

            {error ? (
              <p className="support-choice-popup__error" role="alert">
                {error}
              </p>
            ) : null}

            <button
              type="button"
              className="support-choice-popup__confirm"
              onClick={handleConfirm}
              disabled={
                !selectedLevel
                || confirming
                || (choiceMode === 'change' && options.find((o) => o.level === selectedLevel)?.selectable === false)
              }
              data-resource="public.donation-panel"
              data-action="execute"
              data-route="/api/kofi/my-support-choice"
            >
              {confirming
                ? 'Confirming…'
                : isChangeMode
                  ? 'Confirm level change'
                  : 'Confirm this choice'}
            </button>
          </section>
        </div>
      </div>
    </div>,
    document.body,
      )}
      <RosterProfileThemeCustomizeModal
        isOpen={themeModalOpen}
        onClose={() => setThemeModalOpen(false)}
      />
    </>
  );
}
